(function (root, factory) {
	const api = factory(root);
	if (typeof exports === "object" && typeof module !== "undefined") module.exports = api;
	if (root?.document) api.init();
})(typeof window !== "undefined" ? window : null, function (windowObject) {
	"use strict";
	const dataLayer = (windowObject && (windowObject.dataLayer = windowObject.dataLayer || [])) || [];
	const gtag = function () { dataLayer.push(arguments); };
	let marketingConsent = false;
	let consentResolved = false;
	const pendingEvents = [];
	const CONSENT_WAIT_MS = 3000;
	const pendingMetaEvents = [];
	const sentMetaEvents = new Set();

	function flushMetaCheckoutEvents() {
		const pixelId = windowObject?.ardexaTracking?.metaPixelId;
		if (!marketingConsent || !/^\d+$/.test(String(pixelId || "")) || !pendingMetaEvents.length) return;
		if (!windowObject.fbq) {
			const fbq = function () {
				if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
				else fbq.queue.push(arguments);
			};
			fbq.push = fbq;
			fbq.loaded = true;
			fbq.version = "2.0";
			fbq.queue = [];
			windowObject.fbq = fbq;
			windowObject._fbq = windowObject._fbq || fbq;
			loadScript("https://connect.facebook.net/en_US/fbevents.js");
		}
		if (!windowObject.ardexaMetaCheckoutInitialized) {
			windowObject.fbq("consent", "grant");
			const existing = windowObject.fbq.getState?.()?.pixels?.some((pixel) => String(pixel.id) === String(pixelId)) ||
				windowObject.fbq.queue?.some((args) => args[0] === "init" && String(args[1]) === String(pixelId));
			if (!existing) {
				windowObject.fbq("set", "autoConfig", false, pixelId);
				windowObject.fbq("init", pixelId);
			}
			windowObject.ardexaMetaCheckoutInitialized = true;
		}
		while (pendingMetaEvents.length) {
			const { event, payload } = pendingMetaEvents.shift();
			const key = `${event}:${payload?.event_id || ""}`;
			if (sentMetaEvents.has(key)) continue;
			const commerce = payload?.ecommerce || {};
			windowObject.fbq("trackSingle", pixelId, event === "purchase" ? "Purchase" : "InitiateCheckout", {
				content_ids: (commerce.items || []).map((item) => item.item_id),
				content_type: "product", value: commerce.value, currency: commerce.currency || "EUR",
			}, payload?.event_id ? { eventID: payload.event_id } : undefined);
			sentMetaEvents.add(key);
		}
	}

	function ecommerce(items, value, currency) {
		return { currency: currency || "EUR", value: Number(value || 0), items: items || [] };
	}

	function track(event, payload) {
		if (!event) return;
		// Shopify restores stored consent only after DOMContentLoaded: hold events so GTM tags see it.
		if (windowObject?.document && !consentResolved) {
			pendingEvents.push([event, payload]);
			return;
		}
		dataLayer.push({ ecommerce: null });
		dataLayer.push({ event: event, ...(payload || {}) });
		// Shopify's app pixel owns storefront events; bridge only the custom checkout.
		if (["begin_checkout", "purchase"].includes(event) &&
			windowObject?.document?.querySelector("[data-ardexa-checkout], [data-checkout-success]")) {
			pendingMetaEvents.push({ event, payload });
			flushMetaCheckoutEvents();
		}
	}

	function shopifyConsent(consent) {
		const granted = (value) => value === true || value === "yes" || value === "granted";
		return {
			statistics: granted(consent?.analytics ?? consent?.analyticsAllowed),
			marketing: granted(consent?.marketing ?? consent?.marketingAllowed),
		};
	}

	function updateConsent(consent) {
		marketingConsent = consent?.marketing === true;
		if (windowObject?.ardexaMetaCheckoutInitialized) {
			windowObject.fbq("consent", marketingConsent ? "grant" : "revoke");
		}
		gtag("consent", "update", {
			analytics_storage: consent?.statistics ? "granted" : "denied",
			ad_storage: consent?.marketing ? "granted" : "denied",
			ad_user_data: consent?.marketing ? "granted" : "denied",
			ad_personalization: consent?.marketing ? "granted" : "denied",
		});
		releaseEvents();
		flushMetaCheckoutEvents();
	}

	function releaseEvents() {
		consentResolved = true;
		while (pendingEvents.length) track(...pendingEvents.shift());
	}

	function bindShopifyConsent() {
		if (!windowObject?.document) return;
		const sync = (event) => {
			const consent = event?.detail || windowObject.Shopify?.customerPrivacy?.currentVisitorConsent?.();
			if (consent) updateConsent(shopifyConsent(consent));
		};
		const loadPrivacy = () => {
			if (!windowObject.Shopify?.customerPrivacy && windowObject.Shopify?.loadFeatures) {
				windowObject.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
					if (error) releaseEvents();
					else sync();
				});
			} else sync();
		};
		windowObject.document.addEventListener("visitorConsentCollected", sync);
		windowObject.addEventListener?.("load", loadPrivacy, { once: true });
		loadPrivacy();
	}

	function loadScript(src, attributes) {
		const script = windowObject.document.createElement("script");
		script.src = src;
		script.async = true;
		Object.entries(attributes || {}).forEach(([key, value]) => script.setAttribute(key, value));
		windowObject.document.head.appendChild(script);
	}

	function loadIntegrations(config) {
		if (!windowObject.ardexaGtmLoaded) {
			gtag("consent", "default", {
				analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied",
				functionality_storage: "granted", security_storage: "granted", wait_for_update: CONSENT_WAIT_MS,
			});
		}
		if (config.cookiebotId) {
			loadScript("https://consent.cookiebot.com/uc.js", {
				id: "Cookiebot", "data-cbid": config.cookiebotId, "data-blockingmode": "auto", type: "text/javascript",
			});
			windowObject.addEventListener("CookiebotOnConsentReady", () => updateConsent(windowObject.Cookiebot?.consent));
			windowObject.addEventListener("CookiebotOnAccept", () => updateConsent(windowObject.Cookiebot?.consent));
			windowObject.addEventListener("CookiebotOnDecline", () => updateConsent(windowObject.Cookiebot?.consent));
		}
		if (config.gtmId && !windowObject.ardexaGtmLoaded) {
			dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
			loadScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(config.gtmId)}`);
			windowObject.ardexaGtmLoaded = true;
		}
	}

	function initPage(context) {
		if (!context) return;
		if (context.pageType === "product" && context.items?.[0]) {
			track("view_item", { ecommerce: ecommerce(context.items, context.value, context.currency) });
		} else if (context.pageType === "collection" && context.items?.length) {
			track("view_item_list", { ecommerce: { item_list_id: context.listId, item_list_name: context.listName, items: context.items } });
		} else if (context.pageType === "cart" && context.items?.length) {
			track("view_cart", { ecommerce: ecommerce(context.items, context.value, context.currency) });
		}
	}

	function bindClicks(context) {
		windowObject.document.addEventListener("click", (event) => {
			const link = event.target.closest?.('a[href*="/products/"]');
			if (link && context?.pageType === "collection") {
				const item = context.items?.find((candidate) => link.href.includes(candidate.item_handle));
				if (item) track("select_item", { ecommerce: { item_list_id: context.listId, item_list_name: context.listName, items: [item] } });
			}
			const checkout = event.target.closest?.('[data-ardexa-checkout-trigger], a[href="/checkout"]');
			if (checkout && context?.items?.length) track("begin_checkout", { ecommerce: ecommerce(context.items, context.value, context.currency) });
		});
		windowObject.document.addEventListener("submit", (event) => {
			if (!event.target.matches?.('form[action*="/cart/add"]')) return;
			const item = context?.items?.[0];
			if (item) track("add_to_cart", { ecommerce: ecommerce([item], item.price, context.currency) });
		});
	}

	function init() {
		const config = windowObject.ardexaTracking || {};
		loadIntegrations(config);
		bindShopifyConsent();
		// Without an answer from the privacy API, send events under the denied defaults.
		windowObject.setTimeout?.(releaseEvents, CONSENT_WAIT_MS);
		const start = () => { initPage(windowObject.ardexaTrackingContext); bindClicks(windowObject.ardexaTrackingContext); };
		if (windowObject.document.readyState === "loading") windowObject.document.addEventListener("DOMContentLoaded", start, { once: true });
		else start();
	}

	if (windowObject) windowObject.ArdexaTracking = { ecommerce, track, updateConsent, initPage };
	return { ecommerce, track, updateConsent, shopifyConsent, initPage, loadIntegrations, init };
});
